import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Popover, PopoverContent, PopoverTrigger, Modal, ModalContent, ModalBody } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import {
  AppGroup,
  AppPlatform,
  appPlatformCaption,
  appPlatforms,
  applyAppGroup,
  copyAppGroup,
  findAppGroup,
  getNewAppGroup,
  groupApps,
  hasAnyPlatformId,
  installIdPlatforms,
  removeAppGroup,
} from '@/features/apps/appGroups';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { NumberText } from '@/components/Common/Field';
import { DeleteModal } from '@/components/Common/Modals';

import { CatHeader } from '@/pages/Projects/Project';
import { RoleContext } from '@/app/api/common';
import { usePageSubscription } from '@/app/websocket/hooks';

// /apps.json holds one row per (app, platform); this page shows and edits one
// row per app name. See features/apps/appGroups.ts for the merge rules.
const Apps = () => {
  const { t } = useTranslation();

  usePageSubscription([{ type: 'app' }]);

  const apps = useAppSelector((state) => state.apps);
  const navigate = useNavigate();
  const { app_id } = useParams();

  const [cols, setCols] = React.useState([] as any[]);
  const [deleteName, setDeleteName] = React.useState(null as string | null);
  const role = React.useContext(RoleContext);

  const groups = React.useMemo(() => groupApps(apps.apps), [apps.apps]);

  const isNew = app_id === '__new__';
  const selected = React.useMemo(() => {
    if (!app_id || isNew)
      return null;
    return findAppGroup(groups, app_id);
  }, [app_id, isNew, groups]);

  React.useEffect(() => {
    store.getApps();
  }, []);

  React.useEffect(() => {
    setCols([
    {
        field:			"name",
        headerName:		t("apps:app:name:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    ...appPlatforms.map((p: AppPlatform) => ({
        field:			p,
        headerName:		t(appPlatformCaption[p]),
        flex:			0,
        minWidth:		100,
        maxWidth:		120,
    })),
    {
        field:			"logo",
        headerName:		t("apps:app:logo:caption"),
        flex:			2,
        minWidth:		250,
    },
    ...(role.admin || role?.privilages?.includes("ChangeApps") || role?.privilages?.includes("DeleteApps") ? [{
        field:			"options",
        headerName:		t("apps:app:options:caption"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] : []),
    ]);
  }, [t, role.admin, role?.privilages]);

  const rows = React.useMemo(() => groups.map((group: AppGroup) => {
    const row: any = {
      name:				group.name,
      logo:				group.logo,
      requestDelete:	() => setDeleteName(group.name),
      requestEdit:		() => navigate('/settings/apps/' + encodeURIComponent(group.name)),
    };
    for (const p of appPlatforms)
      row[p] = group.platforms[p]?.id ? t('apps:app:yes') : '';
    return row;
  }), [groups, navigate, t]);

  const updateIsOpen = (val: boolean) => {
    if (!val)
      navigate('/settings/apps');
  };

  const processDelete = React.useCallback(async (obj: Object, answer: string) => {
    const name = deleteName;
    setDeleteName(null);

    if (answer !== "yes" || name === null)
      return;

    const mess = await store.setApps({ apps: removeAppGroup(apps.apps, name) });
    console.log("Apps/processDelete/name=" + name + "/mess=", mess);
  }, [apps.apps, deleteName]);

  const updateApp = React.useCallback(async (oldName: string | null, group: AppGroup, logoChanged: boolean) => {
    const mess = await store.setApps({ apps: applyAppGroup(apps.apps, oldName, group, logoChanged) });
    console.log("Apps/updateApp/oldName=" + oldName + "/name=" + group.name + "/mess=", mess);
  }, [apps.apps]);

  const handleCellClick = React.useCallback((evt: any) => {
    if (evt.colDef.field == "options")
      return;
    if(!role.admin && !role?.privilages?.includes("ViewApps"))
      return
    navigate('/settings/apps/' + encodeURIComponent(evt.data.name));
  }, [navigate, role.admin, role?.privilages]);

  return (
  <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('apps:caption') }
      </div>
      <div>
        <NumberText>
          { rows.length }
        </NumberText>
        { ' ' +
          t('apps:apps')
        }
      </div>
      <Divider className="my-[8px]" />
      { (role.admin || role?.privilages?.includes("ChangeApps")) &&
        <Button caption={t('apps:new_app')}
                icon="add"
                className="uppercase mt-[20px]"
                onClick={() => navigate('/settings/apps/__new__')} />
      }
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               "px-[32px]"
             ]}>

      <Grid rowData={rows}
            columnDefs={cols}
            filter={true}
            globalFilter={true}
            showPages={true}
            maxHeight="calc(100vh - 380px)"
            onCellClicked={handleCellClick}
            pageSize={10} />
    </FlexRow>
  {(isNew || selected) &&
    <Modal backdrop="opaque"
           defaultOpen={true}
           isDismissable={false}
           onOpenChange={updateIsOpen}
           classNames={{
             backdrop: "bg-[#0000007f]",
             wrapper: "justify-end"
           }}
           hideCloseButton={true}
           radius="none"
           placement="top">
      <ModalContent className="h-full !mx-0 !my-0 w-full max-w-[620px]">
        <ModalBody className="w-full overflow-y-auto pb-[120px]">
          <FlexRow direction="column"
                   flex={0}>
            <App app={isNew ? getNewAppGroup() : selected}
                 isNew={isNew}
                 groups={groups}
                 setApp={(group: AppGroup, logoChanged: boolean) => updateApp(isNew ? null : selected!.name, group, logoChanged)}
                 onClose={() => navigate('/settings/apps')} />
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </Modal>
  }
  {deleteName !== null &&
	<DeleteModal title={t('apps:delete_app:caption')}
	             message={t('apps:delete_app:message_n', {n: deleteName})}
	             onResult={processDelete}>
      <>
      </>
    </DeleteModal>
  }
  </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);
  const handleEdit = React.useCallback(() => {
    setOpen(false);
    props.data.requestEdit();
  }, [setOpen, props.data]);

  const handleDelete = React.useCallback(async () => {
    setOpen(false);
    props.data.requestDelete();
  }, [setOpen, props.data]);

  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {(role.admin || role?.privilages?.includes("ChangeApps")) &&
          <MenuItem icon="edit_note"
                    label={t('apps:app:edit_app')}
                    onClick={handleEdit} />
        }
        {(role.admin || role?.privilages?.includes("DeleteApps")) &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('apps:delete_app:caption')}
                    onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export interface AppProps {
	isNew:				boolean;
	app:				AppGroup | null;
	// Every app, to refuse a name another app already has.
	groups:				AppGroup[];
	setApp:				(app: AppGroup, logoChanged: boolean) => any;
	onClose:			() => any;
}

const App = (props: AppProps) => {
  const { t } = useTranslation();

  const [app, setApp] = React.useState(null as AppGroup | null);
  const [changes, setChanges] = React.useState(false);
  const [logoChanged, setLogoChanged] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);

  React.useEffect(() => {
    setApp(props.app ? copyAppGroup(props.app) : null);
  }, [props.app]);

  const nameInvalid = React.useMemo(() => {
    return !app || app.name.trim() == '';
  }, [app]);

  const nameTaken = React.useMemo(() => {
    if (!app)
      return false;
    const name = app.name.trim();
    return props.groups.some((g) => g.name === name && (props.isNew || g.name !== props.app?.name));
  }, [app, props.groups, props.isNew, props.app]);

  const logoInvalid = React.useMemo(() => {
    return !app || app.logo.trim() == '';
  }, [app]);

  const platformsInvalid = React.useMemo(() => {
    return !app || !hasAnyPlatformId(app);
  }, [app]);

  const invalid = nameInvalid || nameTaken || logoInvalid || platformsInvalid;

  const renamed = !props.isNew && !!app && !!props.app && app.name.trim() !== props.app.name;

  const update = React.useCallback((change: (napp: AppGroup) => void) => {
    if (!app)
      return;

    const napp = copyAppGroup(app);
    change(napp);

    setApp(napp);
    setChanges(true);
  }, [app, setApp]);

  const updatePlatform = (p: AppPlatform, field: 'id' | 'install_id', val: string) => update((napp) => {
    const entry = napp.platforms[p] || { id: '', install_id: '' };
    entry[field] = val;
    napp.platforms[p] = entry;
  });

  const handleSave = React.useCallback(() => {
    if (!app || invalid) {
      setTriedSave(true);
      return;
    }

    setTriedSave(false);
    props.setApp(app, logoChanged || props.isNew);
    setChanges(false);
    props.onClose();
  }, [app, invalid, logoChanged, props.setApp, props.isNew]);

  if (!app)
    return;

  return (
  <React.Fragment>
    <FlexRow direction="column"
             align="start"
             className="px-[32px]">
      <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[620px]">
	    <CatHeader caption={props.isNew ? 'apps:app:new_app' : 'apps:app:edit_app'} />

		<Input label={t('apps:app:name:caption') + '*'}
		       isInvalid={(triedSave && nameInvalid) || nameTaken}
		       errorMessage={nameTaken ? t('apps:app:name:taken') : t('apps:app:name:invalid')}
		       description={renamed ? t('apps:app:name:rename_hint') : undefined}
		       value={app.name}
		       onValueChange={(val: string) => update((napp) => { napp.name = val; })} />
		<Input label={t('apps:app:logo:caption') + '*'}
		       isInvalid={triedSave && logoInvalid}
		       errorMessage={t('apps:app:logo:invalid')}
		       value={app.logo}
		       onValueChange={(val: string) => { setLogoChanged(true); update((napp) => { napp.logo = val; }); }} />

	    <CatHeader caption="apps:app:platforms:caption" />
	    {triedSave && platformsInvalid &&
	      <div className="text-[--Warning] text-[12px]">
	        { t('apps:app:platforms:invalid') }
	      </div>
	    }
	    {appPlatforms.map((p: AppPlatform) => (
	      <FlexRow key={p}
	               className="gap-[16px] w-full">
	        <Input label={t('apps:app:platform_id', { platform: t(appPlatformCaption[p]) })}
	               value={app.platforms[p]?.id || ''}
	               onValueChange={(val: string) => updatePlatform(p, 'id', val)} />
	        {installIdPlatforms.includes(p) &&
	          <Input label={t('apps:app:install_id:caption')}
	                 value={app.platforms[p]?.install_id || ''}
	                 onValueChange={(val: string) => updatePlatform(p, 'install_id', val)} />
	        }
	      </FlexRow>
	    ))}
	    {app.other.length > 0 &&
	      <div className="text-[12px] text-gray-500">
	        { t('apps:app:platforms:other', { platforms: app.other.join(', ') }) }
	      </div>
	    }
	  </FlexRow>
    </FlexRow>
    <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
      <Button className="uppercase"
              caption={t('apps:app:cancel')}
              onClick={() => props.onClose()}
              layout="white" />
      <Button caption={t('apps:app:save')}
              icon="save"
              isDisabled={!changes || nameTaken || (triedSave && invalid)}
              onClick={() => handleSave()} />
    </FlexRow>
  </React.Fragment>
  );
};

export default Apps;
